import React, { useState, useMemo } from 'react';
import { Plus, Search, Tag, Edit2, Trash2, Clock, X } from 'lucide-react';
import { useCollections } from '../../hooks/useCollections';

export function CollectionsList() {
  const { collections, loading, error, createCollection, updateCollection, deleteCollection } = useCollections();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCollection, setNewCollection] = useState({
    title: '',
    description: '',
    content: '',
    tags: [] as string[]
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCollection, setEditCollection] = useState({
    title: '',
    description: '',
    content: '',
    tags: [] as string[]
  });

  // Get unique tags across all collections
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    collections.forEach(collection => {
      collection.tags.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [collections]);

  const handleCreate = async () => {
    if (!newCollection.title.trim()) return;

    await createCollection({
      title: newCollection.title.trim(),
      description: newCollection.description.trim(),
      content: newCollection.content.trim(),
      tags: newCollection.tags
    });

    setNewCollection({ title: '', description: '', content: '', tags: [] });
    setShowCreateForm(false);
  };

  const startEditing = (collection: { id: string; title: string; description: string | null; content: string; tags: string[] }) => {
    setEditingId(collection.id);
    setEditCollection({
      title: collection.title,
      description: collection.description || '',
      content: collection.content,
      tags: collection.tags
    });
  };

  const handleUpdate = async () => {
    if (!editingId || !editCollection.title.trim()) return;
    await updateCollection(editingId, {
      title: editCollection.title.trim(),
      description: editCollection.description.trim(),
      content: editCollection.content.trim(),
      tags: editCollection.tags
    });
    setEditingId(null);
    setEditCollection({ title: '', description: '', content: '', tags: [] });
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
    // Clear search when selecting tags
    setSearchQuery('');
  };

  const clearFilters = () => {
    setSelectedTags([]);
    setSearchQuery('');
  };

  const filteredCollections = useMemo(() => {
    return collections.filter(collection => {
      // If there are selected tags, collection must include ALL selected tags
      if (selectedTags.length > 0 && !selectedTags.every(tag => collection.tags.includes(tag))) {
        return false;
      }
      
      // If there's a search query, check title, description, and tags
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          collection.title.toLowerCase().includes(query) ||
          collection.description?.toLowerCase().includes(query) ||
          collection.tags.some(tag => tag.toLowerCase().includes(query))
        );
      }
      
      return true;
    });
  }, [collections, selectedTags, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric"></div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 md:p-8 md:pb-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-midnight">Collections</h1>
        <button 
          onClick={() => setShowCreateForm(true)}
          className="flex items-center px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90"
        >
          <Plus className="h-5 w-5 mr-2" />
          New Collection
        </button>
      </div>

      <div className="space-y-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate" />
          <input
            type="text"
            placeholder="Search collections..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedTags([]); // Clear tag filters when searching
            }}
            className="w-full pl-10 pr-4 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
          />
        </div>

        {/* Tags filter */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Tag className="h-4 w-4 text-slate" />
            <span className="text-sm font-medium text-slate">Filter by tags:</span>
            {selectedTags.length > 0 && (
              <button
                onClick={clearFilters}
                className="text-sm text-crimson hover:text-crimson/80"
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors ${
                  selectedTags.includes(tag)
                    ? 'bg-electric text-white hover:bg-electric/90'
                    : 'bg-sand/20 text-slate hover:bg-sand/30'
                }`}
              >
                {tag}
                {selectedTags.includes(tag) && (
                  <X className="h-3 w-3 ml-1" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {showCreateForm && (
        <div className="mb-8 bg-white rounded-lg shadow-sm border border-sand/20 p-6">
          <h2 className="text-lg font-semibold text-midnight mb-4">New Collection</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Title</label>
              <input
                type="text"
                value={newCollection.title}
                onChange={(e) => setNewCollection({ ...newCollection, title: e.target.value })}
                className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Description</label>
              <input
                type="text"
                value={newCollection.description}
                onChange={(e) => setNewCollection({ ...newCollection, description: e.target.value })}
                className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Content</label>
              <textarea
                value={newCollection.content}
                onChange={(e) => setNewCollection({ ...newCollection, content: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Tags</label>
              <input
                type="text"
                placeholder="Add tags separated by commas"
                onChange={(e) => setNewCollection({
                  ...newCollection,
                  tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                })}
                className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-slate hover:text-midnight"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newCollection.title.trim()}
                className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
              >
                Create Collection
              </button>
            </div>
          </div>
        </div>
      )}

      {editingId && (
        <div className="mb-8 bg-white rounded-lg shadow-sm border border-electric/30 p-6">
          <h2 className="text-lg font-semibold text-midnight mb-4">Edit Collection</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Title</label>
              <input
                type="text"
                value={editCollection.title}
                onChange={(e) => setEditCollection({ ...editCollection, title: e.target.value })}
                className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Description</label>
              <input
                type="text"
                value={editCollection.description}
                onChange={(e) => setEditCollection({ ...editCollection, description: e.target.value })}
                className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Content</label>
              <textarea
                value={editCollection.content}
                onChange={(e) => setEditCollection({ ...editCollection, content: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Tags</label>
              <input
                type="text"
                defaultValue={editCollection.tags.join(', ')}
                onChange={(e) => setEditCollection({
                  ...editCollection,
                  tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                })}
                placeholder="Add tags separated by commas"
                className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setEditingId(null)}
                className="px-4 py-2 text-slate hover:text-midnight"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={!editCollection.title.trim()}
                className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCollections.map(collection => (
          <div
            key={collection.id}
            className="bg-white rounded-lg shadow-sm border border-sand/20 p-6 hover:border-electric/20 transition-colors"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-midnight">{collection.title}</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => startEditing(collection)}
                  className="p-1 text-slate hover:text-electric rounded"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => deleteCollection(collection.id)}
                  className="p-1 text-slate hover:text-crimson rounded"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            {collection.description && (
              <p className="text-slate mb-4">{collection.description}</p>
            )}
            <div className="flex flex-wrap gap-2 mb-4">
              {collection.tags.map((tag, index) => (
                <button
                  key={index}
                  onClick={() => toggleTag(tag)}
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs transition-colors ${
                    selectedTags.includes(tag)
                      ? 'bg-electric text-white'
                      : 'bg-electric/10 text-electric hover:bg-electric/20'
                  }`}
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </button>
              ))}
            </div>
            <div className="text-xs text-slate flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              {new Date(collection.created_at).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>

      {filteredCollections.length === 0 && (
        <div className="text-center py-12 bg-sand/10 rounded-lg">
          <p className="text-slate">
            {selectedTags.length > 0 || searchQuery
              ? 'No collections match your filters'
              : 'No collections yet'}
          </p>
        </div>
      )}
    </div>
  );
}